<script setup lang="ts">
import { ref, watch } from 'vue';
import { useUserStore } from '@/stores/user';
import { useSchoolStore } from '@/stores/school';
import { usePartStore } from '@/stores/part';
import AuthenticationService from '@/services/AuthenticationService';

const props = defineProps<{
    modelValue: boolean;
}>();

const showModal = ref(props.modelValue);
const emit = defineEmits(['update:model-value']);
const isLoading = ref(false);
const isDisabled = ref(false);

watch(() => props.modelValue, (newVal: boolean) => {
    showModal.value = newVal;
});

const closeModal = () => {
    showModal.value = false;
    emit('update:model-value', false);
};

const logOut = async () => {
    isLoading.value = true;
    isDisabled.value = true;

    const userStore = useUserStore();
    userStore.logout();

    const schoolStore = useSchoolStore();
    schoolStore.clearSchool();

    const partStore = usePartStore();
    partStore.clearPart();

    await AuthenticationService.logout().then(() => {
        window.location.replace(`${import.meta.env.VITE_FSUM_APP_URL}/account/?logout=1`);
    }).catch(() => {
        isDisabled.value = false;
        isLoading.value = false;
    });
};

</script>
<template>
    <q-dialog
        v-model="showModal"
        persistent
        @update:model-value="emit('update:model-value', $event)"
    >
        <q-card id="logout-modal">
            <q-toolbar>
                <q-space />
                <q-btn
                    v-close-popup="true"
                    icon="close"
                    flat
                    round
                    dense
                    class="float-right close-btn"
                    :disable="isDisabled"
                    @click="closeModal"
                />
            </q-toolbar>
            <q-card-section class="modal-header text-center q-pt-xs">
                Confirm Log Out
            </q-card-section>
            <q-card-section class="buttons text-center q-pb-lg justify-evenly row">
                <q-btn
                    v-close-popup="true"
                    label="Cancel"
                    class="cancel-btn fsum-button-outlined"
                    no-caps
                    :disable="isDisabled"
                    @click="closeModal"
                />
                <q-btn
                    label="Log Out"
                    class="final-logout-btn fsum-button-dark-blue"
                    no-caps
                    :loading="isLoading"
                    @click="logOut"
                />
            </q-card-section>
        </q-card>
    </q-dialog>
</template>
<style scoped>
#logout-modal {
    width: 400px;
    max-width: 80vw;
}
.modal-header {
    font-size: 24px;
    font-weight: 500;
    color: var(--fsum-dark-blue);
}
.confirmation-text {
    font-size: 16px;
    line-height: 22px;
}
</style>
